import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import SiteLayout from '@/components/feature/SiteLayout';
import Breadcrumb from '@/components/base/Breadcrumb';
import SegmentedControl from '@/components/base/SegmentedControl';
import Pagination from '@/components/base/Pagination';
import EmptyState from '@/components/base/EmptyState';
import { usePageMeta } from '@/hooks/usePageMeta';
import { searchContent, formatDate, type SearchResultType } from '@/lib/content';
import { newsItems } from '@/mocks/news';
import { workItems } from '@/mocks/works';

const PAGE_SIZE = 5;

const typeOptions = [
  { label: '全部', value: 'all' },
  { label: '资讯', value: 'news' },
  { label: '作品', value: 'works' },
  { label: '文化', value: 'culture' },
];

const typeLabel: Record<SearchResultType, string> = {
  news: '资讯',
  works: '作品',
  culture: '文化',
};

export default function SearchPage() {
  const [params, setParams] = useSearchParams();
  const keyword = (params.get('q') ?? '').trim();
  const [input, setInput] = useState(keyword);
  const [type, setType] = useState('all');
  const [visible, setVisible] = useState(PAGE_SIZE);

  usePageMeta({
    title: keyword ? `${keyword} 的搜索结果 - 琉璃神社` : '站内搜索 - 琉璃神社',
    description: keyword
      ? `在琉璃神社搜索“${keyword}”的相关动漫资讯、作品档案与文化专题，快速找到你想了解的内容。`
      : '琉璃神社站内搜索，支持按关键词模糊查询动漫资讯、作品档案与文化专题。',
    keywords: '站内搜索,动漫资讯搜索,作品搜索,琉璃神社',
  });

  useEffect(() => {
    setInput(keyword);
    setVisible(PAGE_SIZE);
  }, [keyword]);

  useEffect(() => {
    setVisible(PAGE_SIZE);
  }, [type]);

  const results = useMemo(() => searchContent(keyword), [keyword]);
  const filtered = useMemo(
    () => (type === 'all' ? results : results.filter((item) => item.type === type)),
    [results, type],
  );

  const shown = filtered.slice(0, visible);
  const currentPage = Math.max(1, Math.ceil(visible / PAGE_SIZE));

  const hotWorks = workItems.slice(0, 3);
  const hotNews = newsItems.slice(0, 3);

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const q = input.trim();
    if (q) {
      setParams({ q });
    } else {
      setParams({});
    }
  };

  return (
    <SiteLayout>
      <div className="w-full bg-background-100 border-b border-background-200/70">
        <div className="w-full px-4 md:px-8 py-8 md:py-12">
          <Breadcrumb items={[{ label: '站内搜索' }]} />
          <div className="mt-5 max-w-3xl">
            <div className="flex items-center gap-2 mb-2.5">
              <span className="w-6 h-px bg-accent-500"></span>
              <span className="text-[11px] tracking-[0.24em] uppercase text-accent-600 font-label">
                Search
              </span>
            </div>
            <h1 className="text-2xl md:text-3xl font-semibold tracking-wide">站内搜索</h1>
            <p className="mt-2.5 text-sm text-secondary-600 leading-6">
              支持关键词模糊查询，覆盖资讯标题、摘要、作品简介、标签与文化专题正文。
            </p>

            <form onSubmit={submit} className="mt-5 w-full">
              <div className="flex flex-col sm:flex-row gap-2.5">
                <div className="flex items-center gap-2 flex-1 h-12 px-4 rounded-md bg-background-50 border border-background-200 focus-within:border-primary-400 transition-colors">
                  <i className="ri-search-line text-secondary-400 text-lg w-5 h-5 flex items-center justify-center"></i>
                  <input
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    placeholder="输入关键词，例如：芙莉莲、赛博朋克、水墨"
                    aria-label="搜索关键词"
                    className="flex-1 min-w-0 bg-transparent text-sm text-foreground-900 placeholder:text-secondary-400 outline-none"
                  />
                  {input && (
                    <button
                      type="button"
                      aria-label="清空关键词"
                      onClick={() => setInput('')}
                      className="text-secondary-400 hover:text-foreground-700 transition-colors cursor-pointer"
                    >
                      <i className="ri-close-circle-line w-4 h-4 flex items-center justify-center"></i>
                    </button>
                  )}
                </div>
                <button
                  type="submit"
                  className="h-12 px-7 rounded-md bg-primary-500 text-background-50 text-sm hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                >
                  搜索
                </button>
              </div>
            </form>

            {keyword && (
              <p className="mt-4 text-xs text-secondary-500">
                当前搜索词：
                <span className="ml-1 text-foreground-900 font-medium">“{keyword}”</span>
              </p>
            )}
          </div>
        </div>
      </div>

      <div className="shell py-8 md:py-12">
        {!keyword ? (
          <div>
            <div className="rounded-lg border border-background-200/80 bg-background-50 p-6 md:p-8">
              <div className="flex items-center gap-3">
                <span className="w-10 h-10 rounded-lg bg-primary-50 text-primary-700 flex items-center justify-center">
                  <i className="ri-search-eye-line text-lg"></i>
                </span>
                <div>
                  <h2 className="text-base font-semibold">还没输入关键词</h2>
                  <p className="mt-0.5 text-xs text-secondary-500">试试上面提到的关键词，或从下面的热门内容开始。</p>
                </div>
              </div>
            </div>

            <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h2 className="text-sm font-semibold">热门作品</h2>
                  <Link
                    to="/category/works"
                    className="text-xs text-primary-600 hover:text-accent-600 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    全部作品
                  </Link>
                </div>
                <div className="space-y-2.5">
                  {hotWorks.map((work) => (
                    <Link
                      key={work.id}
                      to={`/detail/works/${work.id}`}
                      className="group flex items-center gap-3 p-3 rounded-lg border border-background-200/80 bg-background-50 hover:border-primary-300 transition-colors cursor-pointer"
                    >
                      <span className="w-12 h-16 rounded-md overflow-hidden bg-background-200 shrink-0">
                        <img
                          src={work.cover}
                          alt={work.title}
                          title={`${work.title} - 琉璃神社作品介绍`}
                          className="w-full h-full object-cover object-top"
                        />
                      </span>
                      <span className="min-w-0">
                        <span className="block text-sm text-foreground-900 group-hover:text-primary-700 transition-colors truncate">
                          {work.title}
                        </span>
                        <span className="mt-1 block text-[11px] text-secondary-500">
                          {work.type} · {work.region} · {work.year}
                        </span>
                      </span>
                    </Link>
                  ))}
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-3">
                  <h2 className="text-sm font-semibold">热门资讯</h2>
                  <Link
                    to="/category/news"
                    className="text-xs text-primary-600 hover:text-accent-600 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    全部资讯
                  </Link>
                </div>
                <div className="space-y-2.5">
                  {hotNews.map((news) => (
                    <Link
                      key={news.id}
                      to={`/detail/news/${news.id}`}
                      className="group flex items-center gap-3 p-3 rounded-lg border border-background-200/80 bg-background-50 hover:border-primary-300 transition-colors cursor-pointer"
                    >
                      <span className="w-16 h-12 rounded-md overflow-hidden bg-background-200 shrink-0">
                        <img
                          src={news.cover}
                          alt={news.title}
                          title={`${news.title} - 琉璃神社动漫资讯`}
                          className="w-full h-full object-cover object-top"
                        />
                      </span>
                      <span className="min-w-0">
                        <span className="block text-sm text-foreground-900 group-hover:text-primary-700 transition-colors clamp-2">
                          {news.title}
                        </span>
                        <span className="mt-1 block text-[11px] text-secondary-500 font-label">
                          {formatDate(news.date)}
                        </span>
                      </span>
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <SegmentedControl options={typeOptions} value={type} onChange={setType} ariaLabel="结果类型过滤" />
              <p className="text-xs text-secondary-500 whitespace-nowrap">
                共找到 <span className="text-foreground-900 font-label">{filtered.length}</span> 条结果
                {type !== 'all' && (
                  <span className="ml-1 text-secondary-400">
                    （{typeLabel[type as SearchResultType]}）
                  </span>
                )}
              </p>
            </div>

            {shown.length > 0 ? (
              <>
                <div className="mt-5 space-y-3">
                  {shown.map((result) => (
                    <Link
                      key={`${result.type}-${result.id}`}
                      to={result.href}
                      className="group flex gap-4 p-4 rounded-lg border border-background-200/80 bg-background-50 hover:border-primary-300 transition-colors cursor-pointer"
                    >
                      <span
                        className={`shrink-0 rounded-md overflow-hidden bg-background-200 ${
                          result.type === 'works'
                            ? 'w-[76px] h-[100px]'
                            : 'w-[120px] h-[80px] md:w-[148px] md:h-[96px]'
                        }`}
                      >
                        <img
                          src={result.cover}
                          alt={result.title}
                          title={`${result.title} - 琉璃神社`}
                          className="w-full h-full object-cover object-top"
                        />
                      </span>
                      <span className="min-w-0 flex-1 flex flex-col">
                        <span className="flex flex-wrap items-center gap-2">
                          <span className="px-2 py-0.5 rounded bg-primary-50 text-primary-700 text-[10px] whitespace-nowrap">
                            {typeLabel[result.type]}
                          </span>
                          <span className="text-[11px] text-secondary-500 font-label">{result.meta}</span>
                        </span>
                        <span className="mt-1.5 text-sm md:text-[15px] font-medium leading-6 text-foreground-900 group-hover:text-primary-700 transition-colors clamp-2">
                          {result.title}
                        </span>
                        <span className="mt-1.5 text-xs text-secondary-600 leading-6 clamp-2">
                          {result.summary}
                        </span>
                        <span className="mt-auto pt-2 text-[11px] text-primary-600 inline-flex items-center gap-1">
                          查看详情
                          <i className="ri-arrow-right-line w-3.5 h-3.5 flex items-center justify-center"></i>
                        </span>
                      </span>
                    </Link>
                  ))}
                </div>

                {visible < filtered.length && (
                  <div className="mt-7 flex flex-col items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setVisible((prev) => prev + PAGE_SIZE)}
                      className="inline-flex items-center gap-2 h-11 px-7 rounded-md border border-background-200 bg-background-50 text-sm text-foreground-800 hover:border-primary-300 hover:text-primary-700 transition-colors whitespace-nowrap cursor-pointer"
                    >
                      加载更多结果
                      <i className="ri-arrow-down-line w-4 h-4 flex items-center justify-center"></i>
                    </button>
                    <span className="text-[11px] text-secondary-400 font-label">
                      已显示 {shown.length} / {filtered.length}
                    </span>
                  </div>
                )}

                <Pagination
                  total={filtered.length}
                  pageSize={PAGE_SIZE}
                  page={currentPage}
                  onChange={(page) => setVisible(page * PAGE_SIZE)}
                />
              </>
            ) : (
              <div className="mt-6">
                <EmptyState
                  icon="ri-search-line"
                  title={`没有找到与“${keyword}”相关的内容`}
                  description="换个更简短的关键词试试，或把结果类型切换为“全部”。下面是编辑推荐的热门内容。"
                  actionLabel="查看全部资讯"
                  actionTo="/category/news"
                />
              </div>
            )}

            {shown.length === 0 && (
              <div className="mt-8">
                <h2 className="text-sm font-semibold mb-3">编辑推荐</h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  {workItems.slice(0, 3).map((work) => (
                    <Link
                      key={work.id}
                      to={`/detail/works/${work.id}`}
                      className="group flex items-center gap-3 p-3 rounded-lg border border-background-200/80 bg-background-50 hover:border-primary-300 transition-colors cursor-pointer"
                    >
                      <span className="w-12 h-16 rounded-md overflow-hidden bg-background-200 shrink-0">
                        <img
                          src={work.cover}
                          alt={work.title}
                          title={`${work.title} - 琉璃神社作品介绍`}
                          className="w-full h-full object-cover object-top"
                        />
                      </span>
                      <span className="min-w-0">
                        <span className="block text-sm text-foreground-900 group-hover:text-primary-700 transition-colors truncate">
                          {work.title}
                        </span>
                        <span className="mt-1 block text-[11px] text-secondary-500">{work.type}</span>
                      </span>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </SiteLayout>
  );
}